import { supabase } from './supabase';
import { API_URL } from '../config';

/**
 * Refresh the current session if needed
 */
export const refreshSession = async (): Promise<string | null> => {
  try {
    const { data: { session }, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error('Error refreshing session:', error);
      return null;
    }
    
    return session?.access_token || null;
  } catch (error) {
    console.error('Exception refreshing session:', error);
    return null;
  }
};

/**
 * Get the current user's JWT token from Supabase
 */
export const getAuthToken = async (): Promise<string | null> => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Error getting session:', error);
      return null;
    }
    
    if (!session) {
      console.warn('No active session found');
      return null;
    }
    
    if (!session.access_token) {
      console.warn('Session exists but no access token found');
      return null;
    }
    
    // Check if token is close to expiring (within 5 minutes)
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = session.expires_at || 0;
    
    if (expiresAt - now < 300) { // 5 minutes
      console.log('Token expiring soon, refreshing...');
      const refreshedToken = await refreshSession();
      if (refreshedToken) {
        return refreshedToken;
      }
    }
    
    return session.access_token;
  } catch (error) {
    console.error('Exception in getAuthToken:', error);
    return null;
  }
};

/**
 * Make an authenticated API request with JWT token
 */
export const authenticatedFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('No authentication token available');
  }

  const headers = {
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  return fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });
};

/**
 * Make an authenticated API request and return JSON
 */
export const authenticatedRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await authenticatedFetch(endpoint, options);
  
  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    
    try {
      // Clone the response to avoid consuming the body multiple times
      const responseClone = response.clone();
      const errorData = await responseClone.json();
      console.log('Error response data:', JSON.stringify(errorData, null, 2)); // Better debug log
      
      // Check for our standardized error format with "message" field
      if (errorData.message && typeof errorData.message === 'string') {
        errorMessage = errorData.message;
        console.log('Extracted message directly:', errorMessage);
      }
      // Check if the detail object contains a message
      else if (errorData.detail && typeof errorData.detail === 'object' && errorData.detail.message && typeof errorData.detail.message === 'string') {
        errorMessage = errorData.detail.message;
        console.log('Extracted message from detail object:', errorMessage);
      }
      // Fallback for older formats
      else if (errorData.detail && typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
        console.log('Using detail string directly:', errorMessage);
      }
      else if (errorData.error && typeof errorData.error === 'string') {
        errorMessage = errorData.error;
        console.log('Using error string directly:', errorMessage);
      }
      // If message/detail/error are objects, try to extract meaningful info
      else if (errorData.message && typeof errorData.message === 'object') {
        console.log('Message is an object:', errorData.message);
        if (errorData.message.message) {
          errorMessage = String(errorData.message.message);
        } else {
          const stringified = JSON.stringify(errorData.message);
          if (stringified !== '{}') {
            errorMessage = `Error details: ${stringified}`;
          }
        }
      }
      else if (errorData.detail && typeof errorData.detail === 'object') {
        console.log('Detail is an object:', errorData.detail);
        if (errorData.detail.message) {
          errorMessage = String(errorData.detail.message);
        } else {
          const stringified = JSON.stringify(errorData.detail);
          if (stringified !== '{}') {
            errorMessage = `Error details: ${stringified}`;
          }
        }
      }
      else if (errorData.error && typeof errorData.error === 'object') {
        console.log('Error is an object:', errorData.error);
        if (errorData.error.message) {
          errorMessage = String(errorData.error.message);
        } else {
          const stringified = JSON.stringify(errorData.error);
          if (stringified !== '{}') {
            errorMessage = `Error details: ${stringified}`;
          }
        }
      }
      else {
        // Last resort - try to find any meaningful string
        console.log('Trying to extract any meaningful string from error data');
        const stringified = JSON.stringify(errorData);
        if (stringified && stringified !== '{}') {
          errorMessage = `Error: ${stringified}`;
        }
      }
    } catch (jsonError) {
      console.log('JSON parsing failed, trying text:', jsonError);
      // If JSON parsing fails, try to read as text
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = errorText;
        }
      } catch (textError) {
        // Keep default error message
        console.log('Text parsing also failed:', textError);
      }
    }
    
    // Ensure errorMessage is always a string
    if (typeof errorMessage !== 'string') {
      console.error('Error message is not a string:', errorMessage, typeof errorMessage);
      errorMessage = 'An error occurred while processing your request.';
    }
    
    console.log('Final error message:', errorMessage, typeof errorMessage); // Debug log
    throw new Error(errorMessage);
  }
  
  return response.json();
};

/**
 * Upload a file with authentication
 */
export const authenticatedFileUpload = async (
  endpoint: string,
  formData: FormData
): Promise<any> => {
  // Check if API_URL is configured
  if (!API_URL || API_URL === 'undefined') {
    console.error('API_URL is not configured. Current value:', API_URL);
    console.error('Please set REACT_APP_API_ROUTE environment variable.');
    throw new Error('API endpoint is not configured. Please set REACT_APP_API_ROUTE environment variable or contact support.');
  }

  const fullUrl = `${API_URL}${endpoint}`;
  console.log('Making request to:', fullUrl);
  console.log('API_URL value:', API_URL);

  let token = await getAuthToken();
  
  if (!token) {
    console.warn('No token available, attempting to refresh session...');
    token = await refreshSession();
  }
  
  if (!token) {
    throw new Error('No authentication token available. Please log in again.');
  }

  let response: Response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });
  } catch (networkError: any) {
    console.error('Network error during file upload:', networkError);
    console.error('Error details:', {
      name: networkError?.name,
      message: networkError?.message,
      stack: networkError?.stack,
      API_URL: API_URL,
      endpoint: endpoint
    });
    
    // Handle specific network error types
    const errorMessage = networkError?.message || networkError?.toString() || 'Unknown network error';
    
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Load failed')) {
      // This usually means CORS, server down, or network issue
      throw new Error(`Unable to connect to the server at ${API_URL}. Please check:\n1. The backend server is running\n2. Your internet connection\n3. The API URL is correct`);
    }
    
    if (errorMessage.includes('NetworkError') || errorMessage.includes('Network request failed')) {
      throw new Error('Network request failed. Please check your internet connection and try again.');
    }
    
    throw new Error(`Network error: ${errorMessage}`);
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    
    try {
      // Clone the response to avoid consuming the body multiple times
      const responseClone = response.clone();
      const errorData = await responseClone.json();
      console.log('Upload error response data:', JSON.stringify(errorData, null, 2)); // Better debug log
      
      // Check for our standardized error format with "message" field
      if (errorData.message && typeof errorData.message === 'string') {
        errorMessage = errorData.message;
        console.log('Extracted message directly:', errorMessage);
      }
      // Check if the detail object contains a message
      else if (errorData.detail && typeof errorData.detail === 'object' && errorData.detail.message && typeof errorData.detail.message === 'string') {
        errorMessage = errorData.detail.message;
        console.log('Extracted message from detail object:', errorMessage);
      }
      // Fallback for older formats
      else if (errorData.detail && typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
        console.log('Using detail string directly:', errorMessage);
      }
      else if (errorData.error && typeof errorData.error === 'string') {
        errorMessage = errorData.error;
        console.log('Using error string directly:', errorMessage);
      }
      // If message/detail/error are objects, try to extract meaningful info
      else if (errorData.message && typeof errorData.message === 'object') {
        console.log('Message is an object:', errorData.message);
        if (errorData.message.message) {
          errorMessage = String(errorData.message.message);
        } else {
          const stringified = JSON.stringify(errorData.message);
          if (stringified !== '{}') {
            errorMessage = `Error details: ${stringified}`;
          }
        }
      }
      else if (errorData.detail && typeof errorData.detail === 'object') {
        console.log('Detail is an object:', errorData.detail);
        if (errorData.detail.message) {
          errorMessage = String(errorData.detail.message);
        } else {
          const stringified = JSON.stringify(errorData.detail);
          if (stringified !== '{}') {
            errorMessage = `Error details: ${stringified}`;
          }
        }
      }
      else if (errorData.error && typeof errorData.error === 'object') {
        console.log('Error is an object:', errorData.error);
        if (errorData.error.message) {
          errorMessage = String(errorData.error.message);
        } else {
          const stringified = JSON.stringify(errorData.error);
          if (stringified !== '{}') {
            errorMessage = `Error details: ${stringified}`;
          }
        }
      }
      else {
        // Last resort - try to find any meaningful string
        console.log('Trying to extract any meaningful string from error data');
        const stringified = JSON.stringify(errorData);
        if (stringified && stringified !== '{}') {
          errorMessage = `Error: ${stringified}`;
        }
      }
    } catch (jsonError) {
      console.log('JSON parsing failed, trying text:', jsonError);
      // If JSON parsing fails, try to read as text
      try {
        const errorText = await response.text();
        if (errorText) {
          errorMessage = errorText;
        }
      } catch (textError) {
        // Keep default error message
        console.log('Text parsing also failed:', textError);
      }
    }
    
    // Ensure errorMessage is always a string
    if (typeof errorMessage !== 'string') {
      console.error('Upload error message is not a string:', errorMessage, typeof errorMessage);
      errorMessage = 'An error occurred while uploading your file.';
    }
    
    console.log('Final upload error message:', errorMessage, typeof errorMessage); // Debug log
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * API endpoints with authentication
 */
export interface UploadLeadMetadata {
  firstName: string;
  lastName: string;
  contactEmail: string;
  heardAbout: string;
  linkedinUrl: string;
  shareContactInfo: boolean;
  sharePitchDeck: boolean;
  agreementAccepted: boolean;
}

export const api = {
  // Application endpoints
  getApplication: (applicationId: string) =>
    authenticatedRequest(`/get/application/${applicationId}`),
  
  getUserApplications: (userId: string) =>
    authenticatedRequest(`/get/user_applications/${userId}`),
  
  updateApplication: (applicationId: string, data: { question_id: string; answer: string }) =>
    authenticatedRequest(`/patch/update_application/${applicationId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  
  judgeQuestion: (applicationId: string, data: { question_id: string; question: string; answer: string | null }) =>
    authenticatedRequest(`/post/judge_question/${applicationId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  
  getFeedback: (applicationId: string) =>
    authenticatedRequest(`/get/get_feedback/${applicationId}`),
  
  getIntakeForm: (applicationId: string) =>
    authenticatedRequest(`/get/get_intake_form/${applicationId}`),

  sharePitchDeck: (applicationId: string) =>
    authenticatedRequest(`/post/share_pitch_deck/${applicationId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    }),
  
  // File upload endpoint
  analyzeDeck: (file: File, recaptchaToken: string, leadMetadata?: UploadLeadMetadata) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('recaptchaToken', recaptchaToken);

    if (leadMetadata) {
      formData.append('firstName', leadMetadata.firstName);
      formData.append('lastName', leadMetadata.lastName);
      formData.append('contactEmail', leadMetadata.contactEmail);
      formData.append('heardAbout', leadMetadata.heardAbout);
      formData.append('linkedinUrl', leadMetadata.linkedinUrl);
      formData.append('shareContactInfo', String(leadMetadata.shareContactInfo));
      formData.append('sharePitchDeck', String(leadMetadata.sharePitchDeck));
      formData.append('agreementAccepted', String(leadMetadata.agreementAccepted));
    }
    
    return authenticatedFileUpload('/post/analyze_deck', formData);
  },
  
  // Form submission endpoint
  submitForm: (applicationId: string, recaptchaToken: string) =>
    authenticatedRequest(`/post/submit_form/${applicationId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recaptchaToken }),
    }),
};

/**
 * Handle API errors consistently
 */
export const handleApiError = (error: any): string => {
  console.log('handleApiError called with:', error, typeof error);
  
  // If it's already a proper error message, return it
  if (error && error.message && typeof error.message === 'string') {
    return error.message;
  }
  
  // If it's a string, return it directly
  if (typeof error === 'string') {
    return error;
  }
  
  // If error.message exists but is not a string, convert it
  if (error && error.message) {
    const messageStr = String(error.message);
    if (messageStr !== '[object Object]') {
      return messageStr;
    }
  }
  
  // Try to extract meaningful information from error object
  if (error && typeof error === 'object') {
    // Check for common error properties
    if (error.detail && typeof error.detail === 'string') {
      return error.detail;
    }
    if (error.error && typeof error.error === 'string') {
      return error.error;
    }
    if (error.statusText && typeof error.statusText === 'string') {
      return `${error.status ? `HTTP ${error.status}: ` : ''}${error.statusText}`;
    }
    
    // Try to stringify the object for debugging
    try {
      const serialized = JSON.stringify(error);
      if (serialized && serialized !== '{}') {
        return `Error: ${serialized}`;
      }
    } catch (e) {
      // JSON.stringify failed
    }
  }
  
  // Final fallback - always return a string
  return 'An unexpected error occurred. Please try again.';
}; 